import React from 'react';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import {
  Layout,
  Stack,
  SegmentedSelection,
  HelpLink,
  withTranslation,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  view,
  withInject,
  withAccess,
  errorBoundary,
} from '@piwikpro/platform';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { RouterService } from '@piwikpro/router-crate';

const mapStateToProps = state => ({
  userRole: state.session.userRole as SessionState['userRole'],
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  userPreferences: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const Permissions: React.FC<Props> = ({
  userRole,
  userPreferences,
  routerService,
  t,
}) => {
  const { pathname } = useLocation();
  const params = useParams({ from: '/_secured/administration/apps/$appId' });
  const { appId } = params;
  const listPreferences = new ListPreference(userPreferences.load(), '');

  const permissionsSection = pathname.includes('/permissions/users') || /\/permissions\/?$/.test(pathname)
    ? 'users'
    : 'userGroups';

  const selectionButtons = [
    {
      name: t('apps.details.permissions.users.button'),
      value: 'users',
    },
    {
      name: t('apps.details.permissions.user-groups.button'),
      value: 'userGroups',
    },
  ];

  const handlePermissionsViewChange = (newValue: string) => {
    if (!newValue) {
      return;
    }

    if (newValue === selectionButtons[0].value) {
      routerService.push({
        to: '/administration/apps/$appId/permissions/users/$page/$limit',
        params: { appId, page: '1', limit: String(listPreferences.limit) },
      });
    } else if (newValue === selectionButtons[1].value) {
      routerService.push({
        to: '/administration/apps/$appId/permissions/user-groups/$page/$limit',
        params: { appId, page: '1', limit: String(listPreferences.limit) },
      });
    }
  };

  return (
    <div>
      <Layout>
        <Stack vertical spacing="wide">
          <Stack.Item fill>
            {userRole === 'OWNER' && (
              <SegmentedSelection
                value={permissionsSection}
                fullWidth
                items={selectionButtons}
                onChange={handlePermissionsViewChange}
              />
            )}
          </Stack.Item>
          <Stack.Item fill>
            <Stack vertical horizontalAlignment="middle">
              <Outlet />
              <Stack.Item>
                <HelpLink href="https://help.piwik.pro/support/getting-started/websites-permissions/">
                  Permissions for a site or an app
                </HelpLink>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Layout>
    </div>
  );
};

export default connector(withInject<InjectedProps>({
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'USER', level: 'manage' })(withAccess(withTranslation(['administration'])(
    errorBoundary(Permissions),
  ))),
));
